import type { FC, ReactElement } from 'react';

import { useSidebarPinState } from '@backstage/core-components';
import { Icon } from '@iconify/react';
import { colors } from '@ipen/components';
import { alpha, IconButton, List } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { tss } from 'tss-react/mui';

import type { NavItem } from '../../../../types';

import { NavMenuItem } from '../../../NavMenuItem';
import { useScrollbarObserver } from './hooks/useScrollbarObserver';

const NAV_WIDTH_EXPANDED = 248;
export const NAV_WIDTH_CONDENSED = 64;

const useStyles = tss
  .withParams<{
  isHome: boolean;
  isPinned: boolean;
  scrollable: boolean;
}>()
  .create(({ isHome, isPinned, scrollable, theme }) => {
    let topNavWidth = NAV_WIDTH_CONDENSED;

    if (isHome) {
      topNavWidth = 0;
    }

    if (isPinned) {
      topNavWidth = NAV_WIDTH_EXPANDED;
    }

    return {
      icon: {
        background: 'unset',
        color: theme.palette.common.black,
        margin: theme.spacing(1),
        padding: `12px ${scrollable ? 4 : 12}px`,
      },
      list: {
        flexGrow: 1,
        paddingTop: 0,
        width: NAV_WIDTH_EXPANDED,
      },
      mainNav: {
        background: theme.palette.common.white,
        boxShadow: '0px 3px 6px #00000059',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'absolute',
        transition: theme.transitions.create('width', {
          duration: theme.transitions.duration.enteringScreen,
          easing: theme.transitions.easing.sharp,
        }),
        zIndex: theme.zIndex.appBar + 1,
      },
      mainNavCondensed: {
        backdropFilter: 'blur(6.4px)',
        backgroundColor: alpha(theme.palette.common.white, 0.7),
        overflowX: 'hidden',
        transition: theme.transitions.create('all', {
          duration: theme.transitions.duration.leavingScreen,
          easing: theme.transitions.easing.sharp,
        }),
        width: NAV_WIDTH_CONDENSED,
      },
      mainNavExpanded: {
        overflowX: 'hidden',
        transition: theme.transitions.create('all', {
          duration: theme.transitions.duration.enteringScreen,
          easing: theme.transitions.easing.sharp,
        }),
        width: NAV_WIDTH_EXPANDED,
      },
      mainNavPinned: {
        overflowX: 'hidden',
        overflowY: 'auto',
        width: NAV_WIDTH_EXPANDED,
      },
      navBottom: {
        borderTop: `1px solid ${colors.eonNeutral.color300}`,
        display: 'flex',
      },
      topNav: {
        position: 'relative',
        transition: theme.transitions.create('width', {
          duration: theme.transitions.duration.enteringScreen,
          easing: theme.transitions.easing.sharp,
        }),
        width: topNavWidth,
      },
      versionWrapper: {
        alignSelf: 'center',
        textAlign: 'center',
        transition: theme.transitions.create('opacity', {
          duration: theme.transitions.duration.enteringScreen,
          easing: theme.transitions.easing.sharp,
        }),
        width: '100%',
      },
    };
  });

type Props = {
  activeNavItem?: NavItem;
  navItems?: readonly NavItem[];
  version: ReactElement;
};

export const DesktopNavigation: FC<Props> = ({
  activeNavItem,
  navItems,
  version,
}) => {
  const navRef = useRef<HTMLDivElement | null>(null);

  const { isPinned, toggleSidebarPinState } = useSidebarPinState();
  const [topLevelExpanded, setTopLevelExpanded] = useState(isPinned);
  const scrollable = useScrollbarObserver(navRef.current);

  const expanded = isPinned || topLevelExpanded;

  const location = useLocation();

  const { classes, cx } = useStyles({
    isHome: location.pathname === '/home',
    isPinned,
    scrollable,
  });

  const handleMouseEnter = useCallback(() => {
    if (!isPinned) {
      setTopLevelExpanded(true);
    }
  }, [isPinned]);

  const handleMouseLeave = useCallback(() => {
    if (!isPinned) {
      setTopLevelExpanded(false);
    }
  }, [isPinned]);

  return (
    <div
      className={cx(classes.topNav, {
        scrollable,
      })}
    >
      <nav
        aria-label="Main Navigation"
        className={cx(classes.mainNav, {
          [classes.mainNavCondensed]: !expanded,
          [classes.mainNavExpanded]: !isPinned && expanded,
          [classes.mainNavPinned]: isPinned && expanded,
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={navRef}
      >
        <List className={classes.list}>
          {navItems?.map(item => (
            <NavMenuItem
              active={item === activeNavItem}
              item={item}
              key={item.target}
              onClick={handleMouseLeave}
            />
          ))}
        </List>

        <div className={classes.navBottom}>
          <IconButton
            aria-label={isPinned ? 'Unpin Sidebar' : 'Pin Sidebar'}
            className={classes.icon}
            onClick={toggleSidebarPinState}
          >
            <Icon icon={isPinned ? 'bi:pin-angle-fill' : 'bi:pin-angle'} />
          </IconButton>

          <div
            className={classes.versionWrapper}
            style={{ opacity: expanded ? 1 : 0 }}
          >
            {version}
          </div>
        </div>
      </nav>
    </div>
  );
};
